import React, { useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../styles/footer.css';

const serviceSlugs = [
	{ id: 'ai-ml', slug: 'intelligenza-artificiale' },
	{ id: 'web-dev', slug: 'sviluppo-web' },
	{ id: 'consulting', slug: 'trasformazione-digitale' },
	{ id: 'data-eng', slug: 'data-engineering' },
	{ id: 'cybersec', slug: 'cybersecurity' },
	{ id: 'cloud-auto', slug: 'cloud-automazione' },
];

const Footer = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const currentYear = new Date().getFullYear();

	const handleHashLink = useCallback((e) => {
		e.preventDefault();
		const href = e.currentTarget.getAttribute('href');
		navigate(href);
		const hash = href.replace('/', '').replace('#', '');
		setTimeout(() => {
			const el = document.getElementById(hash);
			if (el) el.scrollIntoView({ behavior: 'smooth' });
			else window.scrollTo({ top: 0, behavior: 'smooth' });
		}, 100);
	}, [navigate]);

	return (
		<footer className="site-footer">
			<div className="footer-main">
				<div className="container-modern">
					<div className="footer-grid">
						{/* Col 1: Brand & Description */}
						<div className="footer-col footer-brand-col">
							<h3 className="footer-brand">
								{t('footer.brandName')}
							</h3>
							<p className="footer-tagline">
								{t('footer.tagline')}
							</p>
							<p className="footer-description">
								{t('footer.description')}
							</p>
							<div className="footer-social">
								<a
									href="https://www.linkedin.com/company/ing-eleonora-bernasconi-studio-di-ingegneria/"
									target="_blank"
									rel="noopener noreferrer"
									aria-label="LinkedIn"
									className="footer-social-link"
								>
									<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
										<path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
									</svg>
								</a>
								<a
									href="https://www.instagram.com/ingegneriabernasconi"
									target="_blank"
									rel="noopener noreferrer"
									aria-label="Instagram"
									className="footer-social-link"
								>
									<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
										<path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
									</svg>
								</a>
							</div>
						</div>

						{/* Col 2: Servizi */}
						<div className="footer-col">
							<h4 className="footer-heading">{t('footer.servicesTitle')}</h4>
							<ul className="footer-links">
								{serviceSlugs.map((s) => (
									<li key={s.slug}>
										<Link to={`/servizi/${s.slug}`}>{t(`footer.serviceNames.${s.id}`)}</Link>
									</li>
								))}
							</ul>
						</div>

						{/* Col 3: Link Utili */}
						<div className="footer-col">
							<h4 className="footer-heading">{t('footer.usefulLinks')}</h4>
							<ul className="footer-links">
								<li><a href="/#home" onClick={handleHashLink}>{t('footer.home')}</a></li>
								<li><a href="/#about" onClick={handleHashLink}>{t('footer.aboutUs')}</a></li>
								<li><a href="/#contact" onClick={handleHashLink}>{t('footer.contacts')}</a></li>
								<li><a href="/#contact" onClick={handleHashLink}>{t('footer.requestConsultation')}</a></li>
								<li><Link to="/privacy-policy">Privacy Policy</Link></li>
								<li><Link to="/cookie-policy">Cookie Policy</Link></li>
							</ul>
						</div>

						{/* Col 4: Contatti */}
						<div className="footer-col">
							<h4 className="footer-heading">{t('footer.contactsTitle')}</h4>
							<ul className="footer-contact-list">
								<li>
									<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
										<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
										<circle cx="12" cy="10" r="3"/>
									</svg>
									<span>{t('footer.addressFull')}</span>
								</li>
								<li>
									<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
										<path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
									</svg>
									<a href="tel:+390656558610">+39 06 5655 8610</a>
								</li>
								<li>
									<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
										<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
										<polyline points="22,6 12,13 2,6"/>
									</svg>
									<a href="mailto:info@ingegneriabernasconi.it">info@ingegneriabernasconi.it</a>
								</li>
								<li>
									<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
										<circle cx="12" cy="12" r="10"/>
										<polyline points="12 6 12 12 16 14"/>
									</svg>
									<span>{t('footer.hours')}</span>
								</li>
							</ul>
						</div>
					</div>
				</div>
			</div>

			{/* Bottom bar */}
			<div className="footer-bottom">
				<div className="container-modern">
					<div className="footer-bottom-content">
						<p className="footer-copyright">
							&copy; {currentYear} <strong>{t('footer.brandName')}</strong> &mdash; {t('footer.copyright')}
						</p>
						<div className="footer-bottom-links">
							<Link to="/privacy-policy">Privacy</Link>
							<span className="footer-dot">&middot;</span>
							<Link to="/cookie-policy">Cookie</Link>
						</div>
					</div>
				</div>
			</div>
		</footer>
	);
};

export default Footer;
